import type { Baseball } from "@knowsball/common";
import { isNil } from "lodash";
import z, { ZodError } from "zod";
import { nextID } from "./utils";

const entityTypes = ["team", "player"] as const;

export type EntityType = (typeof entityTypes)[number];

const operators = ["eq", "ne", "lt", "lte", "gt", "gte"] as const;

export type Operator = (typeof operators)[number];

const ruleTypes = ["leaf", "and", "or"] as const;

export type RuleType = (typeof ruleTypes)[number];

const stats = ["avg", "hr", "rbi", "obp", "slg"] as const;

export type Stat = (typeof stats)[number];

export const statToDisplay = (stat: Stat): string => {
	switch (stat) {
		case "avg":
			return "AVG";
		case "hr":
			return "HR";
		case "rbi":
			return "RBI";
		case "obp":
			return "OBP";
		case "slg":
			return "SLG";
		default:
			return stat;
	}
};

const windows = ["inning", "game"] as const;

export type Window = (typeof windows)[number];

export type RuleStatus = "active" | "cooling" | "paused";

export type CooldownPreset =
	| "none"
	| "15m"
	| "1h"
	| "6h"
	| "24h"
	| "once-per-game"
	| "once";

export interface GameInfo {
	awayTeam: string;
	homeTeam: string;
	awayScore: number | null;
	homeScore: number | null;
	clock: string | null;
	status: string;
}

export interface Notification {
	apps?: string[];
	createdAt: string;
	data: Record<string, unknown>;
	game?: GameInfo | null;
	gamePk: string;
	id: string;
	league?: "MLB" | "NFL";
	message: string;
	observedValue?: unknown;
	readAt: string | null;
	stat?: Stat | null;
	targetValue?: unknown;
	title: string;
}

export interface ParsedRuleResponse {
	ambiguous: {
		phrase: string;
		options: {
			entityType: EntityType;
			entityID: string;
			label: string;
		}[];
	} | null;
	apps: string[];
	condition: ConditionDTO;
	cooldown: CooldownPreset;
	league: "MLB" | "NFL";
	missing: string[];
	notifyAgainAfter: NotifyAgainAfter;
	prompt: string;
	statLabel: string | null;
}

const conditionSchema = z.object({
	entityType: z.enum(entityTypes).optional(),
	entityID: z.string().optional(),
	id: z.number().default(() => nextID()),
	operator: z.enum(operators).optional(),
	over: z.number().optional(),
	ruleType: z.enum(ruleTypes),
	rules: z.array(z.any()).optional(),
	stat: z.enum(stats).optional(),
	value: z.any(),
	window: z.enum(windows).optional(),
});

const leafConditionSchema = z.object({
	entityType: z.enum(entityTypes),
	entityID: z.string(),
	id: z.number(),
	operator: z.enum(operators),
	over: z.number(),
	ruleType: z.literal("leaf"),
	stat: z.enum(stats),
	value: z.any(),
	window: z.enum(windows).optional(),
});

const andConditionSchema = z.object({
	id: z.number(),
	ruleType: z.literal("and"),
	rules: z.array(z.any()),
});

const orConditionSchema = z.object({
	id: z.number(),
	ruleType: z.literal("or"),
	rules: z.array(z.any()),
});

export class Condition {
	private _entityID?: string;
	private _entityType?: EntityType;
	private _id: number;
	private _operator?: Operator;
	private _over?: number;
	private _ruleType: RuleType;
	private _rules?: Condition[];
	private _stat?: Stat;
	private _value: any;
	private _window?: Window;

	constructor(x: z.infer<typeof conditionSchema>) {
		if (x.entityID) {
			this._entityID = x.entityID;
		}

		if (x.entityType) {
			this._entityType = x.entityType;
		}

		if (x.operator) {
			this._operator = x.operator;
		}

		if (!isNil(x.over)) {
			this._over = x.over;
		}

		if (x.rules) {
			this._rules = x.rules.map((child) => {
				if (child instanceof Condition) {
					return child;
				}

				return Condition.fromAny(child);
			});
		}

		if (x.stat) {
			this._stat = x.stat;
		}

		if (x.window) {
			this._window = x.window;
		}

		if (!isNil(x.value)) {
			this._value = x.value;
		}

		this._id = x.id;
		this._ruleType = x.ruleType;
	}

	static copy(other: Condition): Condition {
		const request: z.infer<typeof conditionSchema> = {
			id: nextID(),
			rules: other.rules,
			ruleType: other.ruleType,
			value: other.value,
		};

		if (other.entityID) {
			request.entityID = other.entityID;
		}

		if (other.entityType) {
			request.entityType = other.entityType;
		}

		if (other.operator) {
			request.operator = other.operator;
		}

		if (!isNil(other.over)) {
			request.over = other.over;
		}

		if (other.stat) {
			request.stat = other.stat;
		}

		if (other.window) {
			request.window = other.window;
		}

		return new Condition(request);
	}

	static fromAny(x: any): Condition {
		const validated = conditionSchema.parse(x);

		return new Condition(validated);
	}

	static fromJSON(json: string): Condition {
		return Condition.fromAny(JSON.parse(json));
	}

	toAny(): any {
		const result: any = {
			id: this._id,
			ruleType: this._ruleType,
		};

		if (this._entityID) {
			result.entityID = this._entityID;
		}

		if (this._entityType) {
			result.entityType = this._entityType;
		}

		if (this._operator) {
			result.operator = this._operator;
		}

		if (!isNil(this._over)) {
			result.over = this._over;
		}

		if (this._rules) {
			result.rules = this._rules.map((rule) => rule.toAny());
		}

		if (this._stat) {
			result.stat = this._stat;
		}

		if (!isNil(this._value)) {
			result.value = this._value;
		}

		if (this._window) {
			result.window = this._window;
		}

		return result;
	}

	toJSON(): string {
		return JSON.stringify(this.toAny());
	}

	static resolver(values: any, _context: any, _options: any) {
		const errors: Record<string, { message: string; type: string }> = {};

		function validate(node: any, path: string) {
			if (!node?.ruleType) {
				return;
			}

			const prefix = path ? `${path}.${node.id}` : `${node.id}`;

			if (node.ruleType === "leaf") {
				try {
					leafConditionSchema.parse(node);
				} catch (error) {
					if (error instanceof ZodError) {
						// Combine entityID and entityType
						let entityErrorMessage: string | null = null;

						for (const issue of error.issues) {
							if (
								issue.path.length > 0 &&
								["entityID", "entityType"].includes(issue.path[0] as string)
							) {
								entityErrorMessage = issue.message;

								continue;
							}

							// issue.path is e.g. ["entityType"] or ["operator"]
							const field = issue.path.join(".");
							errors[`${prefix}.${field}`] = {
								message: issue.message,
								type: "validation",
							};
						}

						if (entityErrorMessage) {
							errors[`${prefix}.entity`] = {
								message: entityErrorMessage,
								type: "validation",
							};
						}
					}
				}
			} else if (node.ruleType === "and" || node.ruleType === "or") {
				const schema =
					node.ruleType === "and" ? andConditionSchema : orConditionSchema;

				try {
					schema.parse(node);
				} catch (error) {
					if (error instanceof ZodError) {
						for (const issue of error.issues) {
							// Only capture non-"rules" errors at this level
							// (rules children get validated recursively)
							if (issue.path[0] !== "rules") {
								const field = issue.path.join(".");
								errors[`${prefix}.${field}`] = {
									message: issue.message,
									type: "validation",
								};
							}
						}
					}
				}

				// Recurse into children
				if (Array.isArray(node.rules)) {
					for (const child of node.rules) {
						validate(child, prefix);
					}
				} else {
					// rules is missing or not an array — flag it at this node
					errors[`${prefix}.rules`] = {
						message: "Rules are required for compound conditions",
						type: "validation",
					};
				}
			}
		}

		validate(values, "");

		if (Object.keys(errors).length === 0) {
			return {
				values,
				errors: {},
			};
		}

		return {
			values: {},
			errors,
		};
	}

	get id(): number {
		return this._id;
	}

	get ruleType(): RuleType {
		return this._ruleType;
	}

	set ruleType(newRuleType: RuleType) {
		this._ruleType = newRuleType;
	}

	get rules(): Condition[] {
		if (!this._rules) {
			return [];
		}

		return this._rules;
	}

	get entityID(): string | null {
		if (!this._entityID) {
			return null;
		}

		return this._entityID;
	}

	get entityType(): EntityType | null {
		if (!this._entityType) {
			return null;
		}

		return this._entityType;
	}

	get entity(): string | null {
		if (!(this._entityID && this._entityType)) {
			return null;
		}

		return `${this._entityType}:${this._entityID}`;
	}

	set entity(combined: string | null | undefined) {
		if (!combined) {
			delete this._entityID;
			delete this._entityType;

			return;
		}

		const parts = combined.split(":");

		// TODO: validate

		this._entityID = parts[1];
		this._entityType = parts[0] as EntityType;
	}

	get operator(): Operator | null {
		return this._operator || null;
	}

	set operator(newOperator: Operator | null) {
		if (!newOperator) {
			delete this._operator;

			return;
		}

		this._operator = newOperator;
	}

	get over(): number | null {
		return this._over || null;
	}

	set over(newOver: number | null) {
		if (!newOver) {
			delete this._over;

			return;
		}

		this._over = newOver;
	}

	get stat(): Stat | null {
		return this._stat || null;
	}

	set stat(newStat: Stat | null) {
		if (!newStat) {
			delete this._stat;

			return;
		}

		this._stat = newStat;
	}

	get window(): Window | null {
		return this._window || null;
	}

	set window(newWindow: Window | null) {
		if (!newWindow) {
			delete this._window;

			return;
		}

		this._window = newWindow;
	}

	get value(): any {
		return this._value;
	}

	set value(newValue: any) {
		if (isNil(newValue)) {
			delete this._value;

			return;
		}

		this._value = newValue;
	}

	add(condition: Condition) {
		if (condition.id === this._id) {
			return;
		}

		if (this.ruleType !== "leaf") {
			if (!this._rules) {
				this._rules = [];
			}

			this._rules.push(condition);

			return;
		}

		const child = Condition.copy(this);

		delete this._entityID;
		delete this._entityType;
		delete this._operator;
		delete this._over;
		delete this._stat;
		delete this._value;

		this._rules = [child, condition];
		this.ruleType = "and";
	}

	static makeDefault(): Condition {
		return new Condition({
			id: nextID(),
			operator: "gt",
			over: 1,
			ruleType: "leaf",
			stat: "hr",
			value: 1,
			window: "game",
		});
	}

	addDefault() {
		this.add(Condition.makeDefault());
	}

	replace(condition: Condition) {
		if (condition.id === this._id) {
			this.entity = condition.entity;
			this.operator = condition.operator;
			this.over = condition.over;
			this.ruleType = condition.ruleType;
			this._rules = condition.rules;
			this.stat = condition.stat;
			this.value = condition.value;
			this.window = condition.window;

			return;
		}

		if (this._rules) {
			for (const rule of this._rules) {
				rule.replace(condition);
			}
		}
	}

	remove(id: number) {
		// We can't remove ourselves.
		if (id === this._id) {
			return;
		}

		if (this._rules) {
			const newRules: Condition[] = [];

			for (const rule of this._rules) {
				if (rule.id !== id) {
					rule.remove(id);

					newRules.push(rule);
				}
			}

			this._rules = newRules;
		}

		// If there's only one rule, replace.
		if (this._rules?.length === 1) {
			const child = this._rules[0];

			this._rules = child.rules;
			this.ruleType = child.ruleType;
			this.value = child.value;

			if (child.entityID) {
				this._entityID = child.entityID;
			} else {
				delete this._entityID;
			}

			if (child.entityType) {
				this._entityType = child.entityType;
			} else {
				delete this._entityType;
			}

			if (child.operator) {
				this._operator = child.operator;
			} else {
				delete this._operator;
			}

			if (child.over) {
				this._over = child.over;
			} else {
				delete this._over;
			}

			if (child.stat) {
				this._stat = child.stat;
			} else {
				delete this._stat;
			}

			if (child.window) {
				this._window = child.window;
			} else {
				delete this._window;
			}
		}
	}
}

export interface ConditionDTO {
	entityType?: EntityType;
	entityID?: string;
	id: number;
	operator?: Operator;
	over?: number;
	ruleType: RuleType;
	rules?: any[];
	stat?: Stat;
	value?: any;
	window?: Window;
}

const durations = ["minutes", "hours"] as const;

export type Duration = (typeof durations)[number];

export interface NotifyAgainAfter {
	unit: Duration;
	value: number;
}

export interface RuleDTO {
	apps: string[];
	cooldown: CooldownPreset;
	createdAt: string;
	email: string;
	enabled: boolean;
	id: string;
	notifyAgainAfter: string | null;
	prompt: string;
	rule: ConditionDTO;
	status: RuleStatus;
	userID: string;
}

export interface Rule {
	condition: Condition;
	email: string;
	id: string;
	notifyAgainAfter: NotifyAgainAfter;
	userID: string;
}

export const createRuleFormSchema = z.object({
	notifyAgainAfterUnit: z.enum(durations),
	notifyAgainAfterValue: z.number(),
});

export interface CreateRuleForm {
	notifyAgainAfterUnit: Duration;
	notifyAgainAfterValue: number;
}

export interface CreateRuleRequest {
	apps: string[];
	condition: ConditionDTO;
	cooldown: CooldownPreset;
	enabled: boolean;
	notifyAgainAfter: NotifyAgainAfter;
	prompt: string;
}

export interface UpdateRuleRequest extends CreateRuleRequest {
	id: string;
}

export class UnauthorizedError extends Error {
	constructor() {
		super("Unauthorized");
		this.name = "UnauthorizedError";
	}
}

export class PaymentRequiredError extends Error {
	constructor() {
		super("An active subscription is required");
		this.name = "PaymentRequiredError";
	}
}

export interface Subscription {
	active: boolean;
	status: string | null;
	currentPeriodEnd: string | null;
	trialEnd: string | null;
	cancelAtPeriodEnd: boolean;
	hasCustomer: boolean;
	trialDays: number;
}

export class ApiClient {
	private baseUrl: string;
	private getAccessToken: () =>
		| string
		| null
		| undefined
		| Promise<string | null | undefined>;

	constructor(
		getAccessToken: () =>
			| string
			| null
			| undefined
			| Promise<string | null | undefined>,
		apiUrl: string | undefined = import.meta.env.VITE_API_URL,
	) {
		this.baseUrl = (apiUrl || "https://api.knowitall.team").replace(/\/$/, "");
		this.getAccessToken = getAccessToken;
	}

	async createRule(rule: CreateRuleRequest): Promise<{ id: string }> {
		return this.request<{ id: string }>("/createRule", rule);
	}

	async updateRule(rule: UpdateRuleRequest): Promise<{ id: string }> {
		return this.request<{ id: string }>("/updateRule", rule);
	}

	async deleteRule(id: string): Promise<{ id: string }> {
		return this.request<{ id: string }>("/deleteRule", { id });
	}

	async getNotifications(): Promise<{ notifications: Notification[] }> {
		return this.request<{ notifications: Notification[] }>("/getNotifications");
	}

	async markNotificationRead(
		id: string,
	): Promise<{ id: string; updated: boolean }> {
		return this.request<{ id: string; updated: boolean }>(
			"/markNotificationRead",
			{ id },
		);
	}

	async markAllNotificationsRead(): Promise<{
		ids: string[];
		updated: number;
	}> {
		return this.request<{ ids: string[]; updated: number }>(
			"/markAllNotificationsRead",
			{},
		);
	}

	async parseRule(prompt: string): Promise<ParsedRuleResponse> {
		return this.request<ParsedRuleResponse>("/parseRule", { prompt });
	}

	async setRuleEnabled(
		id: string,
		enabled: boolean,
	): Promise<{ enabled: boolean; id: string; status: RuleStatus }> {
		return this.request<{ enabled: boolean; id: string; status: RuleStatus }>(
			"/setRuleEnabled",
			{ enabled, id },
		);
	}

	async getPlayers(): Promise<{ players: Baseball.Player[] }> {
		return this.request<{ players: Baseball.Player[] }>(`/getPlayers`);
	}

	async getTeams(): Promise<{ teams: Baseball.Team[] }> {
		return this.request<{ teams: Baseball.Team[] }>(`/getTeams`);
	}

	async getRules(): Promise<{ rules: RuleDTO[] }> {
		return this.request<{ rules: RuleDTO[] }>("/getRules");
	}

	async getSubscription(): Promise<Subscription> {
		return this.request<Subscription>("/getSubscription");
	}

	async createCheckoutSession(): Promise<{ url: string }> {
		return this.request<{ url: string }>("/createCheckoutSession");
	}

	async createBillingPortalSession(): Promise<{ url: string }> {
		return this.request<{ url: string }>("/createBillingPortalSession");
	}

	async searchPlayers(query: string): Promise<{ players: Baseball.Player[] }> {
		const searchParams = new URLSearchParams();

		searchParams.append("query", query);

		return this.request<{ players: Baseball.Player[] }>(
			`/searchPlayers?${searchParams.toString()}`,
		);
	}

	async searchTeams(query: string): Promise<{ teams: Baseball.Team[] }> {
		const searchParams = new URLSearchParams();

		searchParams.append("query", query);

		return this.request<{ teams: Baseball.Team[] }>(
			`/searchTeams?${searchParams.toString()}`,
		);
	}

	private async request<T>(path: string, body?: unknown): Promise<T> {
		const token = await this.getAccessToken();
		const response = await fetch(`${this.baseUrl}${path}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...(token && { Authorization: `Bearer ${token}` }),
			},
			body: body !== undefined ? JSON.stringify(body) : undefined,
		});
		if (response.status === 401) {
			throw new UnauthorizedError();
		}
		if (response.status === 402) {
			if (typeof window !== "undefined") {
				window.dispatchEvent(new Event("kb:payment-required"));
			}
			throw new PaymentRequiredError();
		}
		if (!response.ok) {
			const body = await response.json().catch(() => null);
			const message =
				body && typeof body === "object" && "message" in body
					? String(body.message)
					: `API error: ${response.status}`;

			throw new Error(message);
		}
		return response.json();
	}
}
